<template>
  <div v-if="!isMobile" class="w-full bg-white overflow-x-hidden">
    <!-- Hero Section -->
    <section class="h-[50vh] relative bg-gradient-to-r from-[#0D0971] to-[#1a1a8e] bg-cover bg-center">
      <div class="absolute inset-0 bg-black/20"></div>
      <div class="relative z-10 h-full flex flex-col justify-center items-center text-center px-[9%]">
        <h1 class="font-bold text-[3.5vw] text-white mb-[1vw]">
          {{ categoryData?.title || 'Category Details' }}
        </h1>
        <div class="w-[20vw] h-[0.3vw] bg-white/50 mb-[2vw]"></div>
        <button
          @click="goBack"
          class="mt-[2vw] px-[2vw] py-[0.8vw] bg-white text-[#0D0971] font-semibold text-[1.1vw] rounded-lg hover:bg-gray-100 transition-colors duration-300 flex items-center gap-[0.5vw]">
          <svg class="w-[1.2vw] h-[1.2vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>

    <!-- Content Section -->
    <section class="pt-[4vw] pb-[0.5vw]">
      <div class="w-[81vw] mx-auto">
        <!-- Description -->
        <div class="mb-[3vw]">
          <div v-if="Array.isArray(categoryData?.description)" class="space-y-[1.2vw]">
            <p v-for="(paragraph, index) in categoryData.description"
               :key="index"
               class="text-[1.2vw] text-[#444] leading-[1.8]">
              {{ paragraph }}
            </p>
          </div>
          <p v-else class="text-[1.2vw] text-[#444] leading-[1.8]">
            {{ categoryData?.description }}
          </p>
        </div>

        <!-- Two Column Layout for Applications and Certifications -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-[3vw] mb-[3vw]">
          <!-- Applications -->
          <div v-if="categoryData?.applications" class="flex flex-col">
            <h2 class="text-[2.2vw] font-bold text-[#0D0971] mb-[1.5vw] flex items-center gap-[0.8vw]">
              <svg class="w-[2vw] h-[2vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
              </svg>
              Applications
            </h2>
            <div v-if="Array.isArray(categoryData.applications)" class="bg-white rounded-xl shadow-md p-[2vw] border border-gray-100 flex-1">
              <ul class="space-y-[0.8vw]">
                <li v-for="(app, index) in categoryData.applications"
                    :key="index"
                    class="flex items-start gap-[0.8vw] text-[1.1vw] text-[#444] hover:text-[#0D0971] transition-colors duration-200">
                  <span class="text-[#0D0971] font-bold mt-[0.2vw]">•</span>
                  <span>{{ app }}</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Certifications -->
          <div v-if="categoryData?.certifications" class="flex flex-col">
            <h2 class="text-[2.2vw] font-bold text-[#0D0971] mb-[1.5vw] flex items-center gap-[0.8vw]">
              <svg class="w-[2vw] h-[2vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
              Certifications
            </h2>
            <div class="bg-white rounded-xl shadow-md p-[2vw] border border-gray-100 flex-1">
              <ul class="space-y-[0.8vw]">
                <li v-for="(cert, index) in categoryData.certifications"
                    :key="index"
                    class="flex items-start gap-[0.8vw] text-[1.1vw] text-[#444] hover:text-[#0D0971] transition-colors duration-200">
                  <svg class="w-[1.2vw] h-[1.2vw] text-green-600 flex-shrink-0 mt-[0.2vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>{{ cert }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Product Introductions -->
        <div v-if="categoryData?.productIntroductions" class="mt-[4vw]">
          <h2 class="text-[2.8vw] font-bold text-[#0D0971] mb-[2vw] flex items-center gap-[0.8vw]">
            <span class="text-[2vw]">1 |</span>
            {{ categoryData.productIntroductions.title }}
          </h2>

          <!-- Products List -->
          <div v-for="(product, index) in categoryData.productIntroductions.products" :key="index" class="mb-[3vw]">
            <!-- Product Name -->
            <h3 class="text-[2vw] font-bold text-[#0D0971] mb-[1.5vw]">{{ product.name }}</h3>

            <!-- Features and Photos Table -->
            <div class="grid grid-cols-2 border border-gray-300 rounded-lg overflow-hidden">
              <!-- Features Column -->
              <div class="border-r border-gray-300">
                <!-- Features Header -->
                <div class="bg-[#0D0971] text-white text-[1.4vw] font-bold py-[1vw] px-[1.5vw] text-center">
                  {{ t('categoryDetail.tableHeaders.features') }}
                </div>
                <!-- Features Content -->
                <div class="p-[2vw] bg-white min-h-[25vw] flex items-center">
                  <ul class="space-y-[1vw] w-full">
                    <li v-for="(feature, fIndex) in product.features"
                        :key="fIndex"
                        class="flex items-start gap-[0.8vw] text-[1.1vw] text-[#444]">
                      <span class="text-[#0D0971] font-bold mt-[0.2vw]">•</span>
                      <span>{{ feature }}</span>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Photos Column -->
              <div>
                <!-- Photos Header -->
                <div class="bg-[#0D0971] text-white text-[1.4vw] font-bold py-[1vw] px-[1.5vw] text-center">
                  {{ t('categoryDetail.tableHeaders.photos') }}
                </div>
                <!-- Photos Content -->
                <div class="p-[1.5vw] bg-gray-50 min-h-[25vw] flex flex-col gap-[1vw]">
                  <div v-for="(photo, pIndex) in product.photos"
                       :key="pIndex"
                       class="flex-1 bg-white rounded-lg overflow-hidden shadow-sm">
                    <img :src="photo"
                         :alt="`${product.name} photo ${pIndex + 1}`"
                         class="w-full h-full object-cover"
                         @error="handleImageError">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Solutions - Custom Fabrication -->
        <div v-if="categoryData?.solutionsCustomFabrication" class="mt-[4vw]">
          <h2 class="text-[2.8vw] font-bold text-[#0D0971] mb-[1vw] flex items-center gap-[0.8vw]">
            <span class="text-[2vw]">2 |</span>
            {{ categoryData.solutionsCustomFabrication.title }}
          </h2>

          <!-- Description -->
          <p class="text-[1.2vw] text-[#444] mb-[2vw]">
            {{ categoryData.solutionsCustomFabrication.description }}
          </p>

          <!-- Solutions Table -->
          <div class="border border-gray-300 rounded-lg overflow-hidden">
            <!-- Table Header -->
            <div class="grid grid-cols-2 bg-[#0D0971] text-white">
              <div class="text-[1.4vw] font-bold py-[1vw] px-[1.5vw] text-center border-r border-gray-300">
                {{ t('categoryDetail.tableHeaders.solution') }}
              </div>
              <div class="text-[1.4vw] font-bold py-[1vw] px-[1.5vw] text-center">
                {{ t('categoryDetail.tableHeaders.introductoryText') }}
              </div>
            </div>

            <!-- Table Rows -->
            <div v-for="(item, index) in categoryData.solutionsCustomFabrication.solutions"
                 :key="index"
                 class="grid grid-cols-2"
                 :class="{ 'bg-gray-50': index % 2 === 1 }">
              <!-- Solution Column -->
              <div class="py-[1.5vw] px-[1.5vw] border-r border-gray-300 border-t border-gray-300 flex items-center">
                <span class="text-[1.1vw] text-[#333] font-medium">{{ item.solution }}</span>
              </div>

              <!-- Introduction Column -->
              <div class="py-[1.5vw] px-[1.5vw] border-t border-gray-300">
                <ul class="space-y-[0.8vw]">
                  <li v-for="(intro, iIndex) in item.introduction"
                      :key="iIndex"
                      class="flex items-start gap-[0.8vw] text-[1.1vw] text-[#444]">
                    <span class="text-[#0D0971] font-bold mt-[0.2vw]">•</span>
                    <span>{{ intro }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Sections (Partner Strengths & Client Benefits) -->
        <div v-if="categoryData?.sections && categoryData.sections.length > 0">
          <div v-for="(section, sectionIndex) in categoryData.sections" :key="sectionIndex" class="mb-[3vw] mt-[4vw]">
            <!-- Section Title -->
            <h2 class="text-[2.8vw] font-bold text-[#0D0971] mb-[2vw] flex items-center gap-[0.8vw]">
              <span class="text-[2vw]">{{ sectionIndex + 3 }} |</span>
              {{ section.title }}
            </h2>

            <!-- Section Items -->
            <div v-if="section.items && section.items.length > 0" class="space-y-[2vw]">
              <div v-for="(item, index) in section.items" :key="index"
                   class="bg-gradient-to-br from-[#f3F8FF] to-[#e8f0fe] rounded-xl p-[2vw] border-l-4 border-[#0D0971] hover:shadow-lg transition-shadow duration-300">
                <!-- Item Name -->
                <h3 class="text-[1.6vw] font-bold text-[#0D0971]" :class="{ 'mb-[1vw]': item.description }">{{ item.name }}</h3>

                <!-- Item Description (only if exists) -->
                <p v-if="item.description" class="text-[1.2vw] text-[#444] leading-[1.8]">
                  {{ item.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Image Gallery -->
    <section class="">
      <ImageGallery :imageItems="beltConveyorGallery" imageHeightClass="max-h-[80vh]" :imagesPerPage="1" columns="1" />
    </section>

    <!-- Category Not Found -->
    <section v-if="!categoryData" class="py-[8vw]">
      <div class="w-[81vw] mx-auto text-center">
        <h2 class="text-[2.5vw] font-bold text-[#0D0971] mb-[2vw]">{{ t('categoryDetail.categoryNotFound') }}</h2>
        <p class="text-[1.3vw] text-[#666] mb-[3vw]">
          {{ t('categoryDetail.categoryNotFoundMessage') }}
        </p>
        <button
          @click="goBack"
          class="px-[2.5vw] py-[1vw] bg-[#0D0971] text-white font-semibold text-[1.2vw] rounded-lg hover:bg-[#1a1a8e] transition-colors duration-300">
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>
  </div>

  <!-- Mobile Version -->
  <MobileCategoryBeltConveyorDetail v-if="isMobile" />
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import { useHead } from '@vueuse/head';
import { pushToDataLayer } from '../utils/gtm';
import { useChatWidget } from '../utils/chatWidget';
import { useDeviceDetection } from "@/utils/deviceDetection";
import MobileCategoryBeltConveyorDetail from "../components/MobileCategoryBeltConveyorDetail.vue";
import ImageGallery from "../components/ImageGallery.vue";
import { beltConveyorGallery } from "../utils/imageData";
import { getSEO } from '../utils/seoConfig';

const route = useRoute();
const router = useRouter();
const { locale, messages, t } = useI18n();
const { isMobile } = useDeviceDetection();

// Get Belt Conveyor category data from i18n (hardcoded to 'belt-conveyor' category)
const categoryData = computed(() => {
  const categories = messages.value[locale.value]?.sourceProduct?.categorys || [];
  const beltConveyorCategory = categories.find((cat: any) => cat.id === 'belt-conveyor');
  return beltConveyorCategory?.detailContent || null;
});

// Get category title for meta
const categoryTitle = computed(() => {
  const categories = messages.value[locale.value]?.sourceProduct?.categorys || [];
  const beltConveyorCategory = categories.find((cat: any) => cat.id === 'belt-conveyor');
  return beltConveyorCategory?.text || 'Belt Conveyor System Category Details';
});

// 动态多语言 SEO
const seo = computed(() => getSEO(locale.value, 'beltConveyor'));

useHead({
  title: computed(() => seo.value.title),
  meta: [
    { name: 'description', content: computed(() => seo.value.description) },
    { name: 'keywords', content: computed(() => seo.value.keywords) },
    { property: 'og:title', content: computed(() => seo.value.title) },
    { property: 'og:description', content: computed(() => seo.value.description) },
  ],
});

onMounted(() => {
  pushToDataLayer('page_view', {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
  });
});

const goBack = () => {
  router.push('/core-offers/source-product');
  pushToDataLayer('click', {
    category: 'CategoryBeltConveyorDetail',
    label: 'Back to Source Product',
  });
};

const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("CategoryBeltConveyorDetail");
  pushToDataLayer('click', {
    category: 'CategoryBeltConveyorDetail',
    label: 'Contact Us',
  });
};

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement;
  // Prevent infinite loop by checking if already tried to load fallback
  if (!target.dataset.errorHandled) {
    target.dataset.errorHandled = 'true';
    // Use a data URL as fallback to avoid another network request
    target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="18" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EImage not available%3C/text%3E%3C/svg%3E';
  }
};

</script>

<style scoped>
.inquery {
  position: fixed;
  right: 1.5vw;
  top: 46%;
  z-index: 100;
  width: 5vw;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.inquery:hover {
  transform: scale(1.1);
}
</style>

